/** @jsx jsx */

import { jsx } from '@emotion/react';
import { useCallback, useEffect, useMemo, useState } from "react";
import { Equipment } from "../../_shared/interfaces/equipment";
import { Button } from "../../_shared/button";
import { A1ConfigData, A1SensorData, eventStreamUrl, reachable, requestConfig, requestSensorData, setType } from "../../_shared/services/hardware.service";
import { EquipmentType, Model } from "../../_shared/interfaces/model";
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { createEventSource, EventSourceMessage } from 'eventsource-client';
import './a1-registration.css'
import { AIcon as Icon } from '../../_shared/icon';

type TestState = "config" | "test";

interface ITestStepProps {
}

const ConfigureComponent = (props: {
  deviceId: string,
  index: number,
  type?: EquipmentType,
  token: string,
  proceed: () => void,
  cancel: () => void,
  platform: 'm1' | 'msom' | undefined,
}) => {
  const [error, setError] = useState('');

  const [reachableIcon, setReachableIcon] = useState('minus-circle');
  const [reachableColor, setReachableColor] = useState('gray');

  const [configuredIcon, setConfiguredIcon] = useState('minus-circle');
  const [configuredColor, setConfiguredColor] = useState('gray');

  const [reachableStatus, setReachableStatus] = useState('Test Connection');
  const [configStatus, setConfigStatus] = useState('Configure Device');

  const [showReachableButton, setShowReachableButton] = useState(true);
  const [enableReachableButton, setEnableReachableButton] = useState(true);

  const [showConfigButton, setShowConfigButton] = useState(false);
  const [enableConfigButton, setEnableConfigButton] = useState(false);

  const [showContinueButton, setShowContinueButton] = useState(false);
  const [enableCancelButton, setEnableCancelButton] = useState(true);

  const testReachable = useCallback(async () => {
    setError('');
    setReachableStatus('Testing Connection...');
    setReachableColor('black');
    setReachableIcon('loading');
    const failed = (e: string) => {
      setError(e);
      setReachableStatus('Connection Failed');
      setReachableColor('red');
      setReachableIcon('close-circle');
      setEnableReachableButton(true);
      setShowReachableButton(true);
      setEnableCancelButton(true);
    }
    const succeeded = () => {
      setError('');
      setReachableStatus('Connection Succeeded');
      setReachableColor('green');
      setReachableIcon('check-circle');
      setShowReachableButton(false);
      setShowConfigButton(true);
      setEnableConfigButton(true);
      setEnableCancelButton(true);
    }

    try {
      setEnableCancelButton(false);
      setEnableReachableButton(false);

      if (!props.platform) {
        failed('Platform not defined');
        return;
      }

      const result = await reachable(props.platform, props.deviceId);

      if (result.status !== 200) {
        failed(`Connection failed: ${result.statusText}`);
      }
      else if (!result.data.ok) {
        if (!result.data.online) {
          failed("Connection failed: device is offline");
        } else {
          failed(`Connection failed.`)
        }
      } else {
        succeeded();
      }
    } catch (e) {
      failed(`${e}`);
    }
  }, [props.platform, props.deviceId]);

  const configure = useCallback(async () => {
    setError('');
    setConfigStatus('Configuring...');
    setConfiguredColor('black');
    setConfiguredIcon('loading');
    const failed = (e: string) => {
      setError(e);
      setConfigStatus('Configuration Failed');
      setConfiguredColor('red');
      setConfiguredIcon('close-circle');
      setEnableConfigButton(true);
      setShowConfigButton(true);
      setEnableCancelButton(true);
    }
    const succeeded = () => {
      setError('');
      setConfigStatus('Device Configured');
      setConfiguredColor('green');
      setConfiguredIcon('check-circle');
      setShowConfigButton(false);
      setShowContinueButton(true);
      setEnableCancelButton(true);
    };

    try {
      if (!props.platform) {
        failed('No platform defined');
        return;
      }

      const result = await setType(props.platform, props.deviceId, props.index, props.type);
      if (result.status !== 200) {
        if (!result.data.connected) {
          failed('Device is offline.');
        } else {
          failed('Configuration failed with unknown error.');
        }
      } else if (result.data.return_value !== 0) {
        failed(`Configuration failed with error ${result.data.return_value}`);
      }
      else {
        succeeded();
      }
    } catch (e) {
      failed(`${e}`);
    }
  }, [props.deviceId, props.index, props.platform, props.type]);

  return <div className='a1-config-outer'>
    <div className='a1-purple-subheader'>Configure Equipment {props.index}</div>
    <div className='a1-config'>
      <div className='a1-config-status-container'>
        <div className='a1-config-status-item'>
          <Icon type={reachableIcon} style={{ color: reachableColor }} className='a1-config-status-icon' />
          <div className='a1-config-status'>{reachableStatus}</div>
        </div>
        <div className='a1-config-status-item'>
          <Icon type={configuredIcon} style={{ color: configuredColor }} className='a1-config-status-icon' />
          <div className='a1-config-status'>{configStatus}</div>
        </div>
      </div>
      <div className='a1-config-error'>{error}</div>
      <div className='a1-config-buttons'>
        <Button outline title='Back' disabled={!enableCancelButton} onClick={props.cancel} />
        {showReachableButton && <Button disabled={!enableReachableButton} title='Test' onClick={testReachable} />}
        {showConfigButton && <Button disabled={!enableConfigButton} title='Configure' onClick={configure} />}
        {showContinueButton && <Button title='Continue' onClick={props.proceed} />}
      </div>
    </div>
  </div>;
};

const ConfigDataComponent = (props: { data?: A1ConfigData, error?: string }) => {
  const { data, error } = props;

  if (error) {
    return <div className='a1-interactive-content'>{error}</div>
  }

  if (!data) {
    return <div className='a1-interactive-content'>Waiting for configuration data...</div>;
  }

  return <div className='a1-interactive-content'>
    <div className='a1-interactive-label'>Power:</div><div> {data.device_on[0] ? "On" : "Off"}</div>
    <div className='a1-interactive-label'>Type:</div><div> {data.equipment_type}</div>
    <div className='a1-interactive-label'>Motor running:</div><div> {data.motor_running ? "Yes" : "No"}</div>
  </div>;
};

const SensorDataComponent = (props: { data?: A1SensorData, error?: string }) => {
  const { data, error } = props;

  if (error) {
    return <div className='a1-interactive-content'>{error}</div>
  }

  if (!data) {
    return <div className='a1-interactive-content loading'>Waiting for sensor data...</div>;
  }

  return <div className='a1-interactive-content'>
    <div><div className='a1-interactive-label'>Chassis Current:</div> {data.CC} A</div>
    <div><div className='a1-interactive-label'>Ambient Temperature:</div> {data.AT}° F</div>
    <div><div className='a1-interactive-label'>Ambient Humidity:</div> {data.AH}%</div>
    {data.IP !== undefined && <div className='a1-interactive-label'><div>Vacuum Level:</div> {data.IP} inHg</div>}
    {data.TP !== undefined && <div className='a1-interactive-label'><div>Tank Pressure:</div> {data.TP} PSI</div>}
  </div>;
};

const Test = (props: {
  equipment?: Equipment,
  deviceId: string,
  index: number,
  proceed: () => void,
  token: string,
  type?: EquipmentType,
}) => {
  const { deviceId, index, proceed, token, equipment } = props;
  const [configError, setConfigError] = useState('');
  const [sensorError, setSensorError] = useState('');
  const [error, setError] = useState('');

  const [config, setConfigData] = useState(undefined as A1ConfigData | undefined);
  const [sensorData, setSensorData] = useState(undefined as A1SensorData | undefined);


  const onMessage = (props: EventSourceMessage) => {
    const { data, event } = props;
    console.log(`Event: ${event}, data: ${data}`);
    if (event === "ai_config") {
      const parsedData = JSON.parse(data);
      setConfigData(JSON.parse(parsedData.data) as A1ConfigData);
    } else if (event === "ai_sensor_instant") {
      const parsedData = JSON.parse(data);
      setSensorData(JSON.parse(parsedData.data) as A1SensorData);
    } else if (event === "error") {
      const err = `Error in event stream: ${data}`;
      console.error(err);
      setError(err);
    }
  };

  const getSensorData = useCallback(async () => {
    if (!equipment || (equipment.platform !== 'm1' && equipment.platform !== 'msom')) {
      console.error(`Invalid platform ${equipment ? equipment.platform : ''}`);
      return;
    }
    setSensorError('');
    setSensorData(undefined);
    try {
      await requestSensorData(equipment.platform, deviceId, index);
    } catch (e) {
      const err = `Error requesting sensor data: ${e}`;
      console.error(err);
      setSensorError(err);
    }
  }, [equipment, deviceId, index]);

  const getConfigData = useCallback(async () => {
    if (!equipment || (equipment.platform !== 'm1' && equipment.platform !== 'msom')) {
      console.error(`Invalid platform ${equipment ? equipment.platform : ''}`);
      return;
    }
    setConfigData(undefined);
    setConfigError('');
    await setConfigData(undefined);
    try {
      await requestConfig(equipment.platform, deviceId, index);
    } catch (e) {
      const err = `Error requesting configuration: ${e}`;
      console.error(err);
      setConfigError(err);
    }
  }, [equipment, deviceId, index]);

  useEffect(() => {
    if (!equipment || (equipment.platform !== 'm1' && equipment.platform !== 'msom')) {
      console.error(`Invalid platform ${equipment!.platform}`);
      return;
    }
    const eventSource = createEventSource({
      url: eventStreamUrl(equipment.platform, deviceId, index),
      headers: { Authorization: `Bearer ${token}` },
      onMessage
    })

    getConfigData();
    getSensorData();

    return () => {
      eventSource.close();
    }
  }, [deviceId, equipment, getConfigData, getSensorData, index, token]);

  return (<div className='a1-installation-interactive'>

    <div className='a1-test-header'>
      <div className='a1-purple-subheader'>Configuring Equipment {equipment && equipment.equipmentSN || props.index}</div>
      <div className='a1-purple-subheader'>Equipment Test</div>
    </div>
    <div className='a1-test-subheader'>Please turn the equipment on and off and verify the displayed sensors.</div>

    <div className='a1-interactive-section'>
      <div className='a1-interactive-header'>
        <div>Configuration Data</div>
        <Button className='a1-interactive-reload' icon='reload' onClick={getConfigData} />
      </div>
      <ConfigDataComponent data={config} error={configError} />
    </div>

    <div className='a1-interactive-section'>
      <div className='a1-interactive-header'>
        <div>Sensor Data</div>
        <Button className='a1-interactive-reload' icon='reload' onClick={getSensorData} />
      </div>
      <SensorDataComponent data={sensorData} error={sensorError} />
    </div>

    {error && <div className='a1-error'>{error}</div>}

    <Button title="Proceed" onClick={proceed} />
  </div>)
};

interface A1InstallationTestProps {
  equipment?: Equipment;
  model?: Model;
  confirm: () => void;
  token: string;
  deviceId: string;
  index: number;
  cancel: () => void;
};

export const A1InstallationTest: React.FC<A1InstallationTestProps> = (props: A1InstallationTestProps) => {
  const { confirm, model, token } = props;

  const [testState, setTestState] = useState<TestState>("config");
  const type = useMemo(() => model ? model.type : undefined, [model]);

  const platform = (props.equipment && props.equipment.platform) as 'm1' | 'msom';

  return <div className='a1-installation-test-page'>
    {testState === "config" && <ConfigureComponent
      type={type}
      index={props.index}
      deviceId={props.deviceId}
      platform={platform}
      token={token}
      proceed={() => {
        if (type) {
          return setTestState("test");
        }
        else {
          confirm();
        }
      }}
      cancel={props.cancel}
    />}
    {testState === "test" && <Test
      equipment={props.equipment}
      deviceId={props.deviceId}
      index={props.index}
      type={type}
      proceed={confirm}
      token={token}
    />}
  </div>;
};